import type { ReactElement } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { isPathAllowedForRole, NAV_ROUTES } from '../pages/Home/navRoutes';
import { useAuth } from './authContext';
import { HEIGHT_BELOW_BARS } from './bannerOffset';

export function PrivateRoute({ children }: { children: ReactElement }) {
  const { isInitializing, isAuthenticated } = useAuth();
  const location = useLocation();

  if (isInitializing) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: HEIGHT_BELOW_BARS }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return children;
}

export function BambooRoute({ children }: { children: ReactElement }) {
  return (
    <PrivateRoute>
      <BambooGate>{children}</BambooGate>
    </PrivateRoute>
  );
}

export function SuperAdminRoute({ children }: { children: ReactElement }) {
  return (
    <BambooRoute>
      <SuperAdminGate>{children}</SuperAdminGate>
    </BambooRoute>
  );
}

export function FeatureRoute({ feature, children }: { feature: string; children: ReactElement }) {
  return (
    <BambooRoute>
      <FeatureGate feature={feature}>{children}</FeatureGate>
    </BambooRoute>
  );
}

function FeatureGate({ feature, children }: { feature: string; children: ReactElement }) {
  const { hasFeature } = useAuth();

  if (!hasFeature(feature)) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function SuperAdminGate({ children }: { children: ReactElement }) {
  const { isSuperAdmin } = useAuth();

  if (!isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function BambooGate({ children }: { children: ReactElement }) {
  const { hasBambooAccess, isSuperAdmin, isDistrictAdmin } = useAuth();
  const { pathname } = useLocation();

  if (hasBambooAccess === null) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: HEIGHT_BELOW_BARS }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!hasBambooAccess) {
    return <Navigate to="/" replace />;
  }

  if (!isPathAllowedForRole(pathname, { isSuperAdmin, isDistrictAdmin })) {
    return <Navigate to={NAV_ROUTES.reports} replace />;
  }

  return children;
}
